<script setup lang="ts">
/**
 * Log in (Figma `66:46882` / `66:47196` / `66:47222`).
 *
 * Gating is deliberately thin: both fields non-empty, nothing more (`66:47504`).
 * Password rules are not checked here — an existing account may predate the
 * current requirements, and refusing to even try would lock those users out.
 *
 * On a rejected pair the screen shows one vague banner and underlines *both*
 * fields rather than naming the wrong one: saying "no such user" would turn the
 * form into an account-existence oracle (`66:47510`). The password is kept so a
 * typo can be fixed without retyping.
 */
import { FairButton } from '@/ui/primitives/FairButton'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthModal, useAuthStore } from '@/features/auth/store'
import { mapAuthError } from '@/features/auth/model/mapAuthError'
import { useAuthFormValidation } from '@/features/auth/model/useAuthFormValidation'
import AuthErrorBanner from '@/features/auth/ui/parts/AuthErrorBanner.vue'
import { useAccountGateStore } from '@/features/account/store/useAccountGateStore'
import AuthField from '@/features/auth/ui/parts/AuthField.vue'
import AuthFooterSwitch from '@/features/auth/ui/parts/AuthFooterSwitch.vue'
import AuthSocialRow from '@/features/auth/ui/parts/AuthSocialRow.vue'
import { useFairToast } from '@/ui/patterns/FairToast'

const emits = defineEmits(['closeModal'])

const { t } = useI18n()
const toast = useFairToast()
const authModal = useAuthModal()

const email = ref('')
const password = ref('')

const clientValid = computed(() => email.value.trim() !== '' && password.value !== '')
const form = useAuthFormValidation({ clientValid })
/** Decision 15 — signed out here because the password changed elsewhere (set by the session host). */
const accountGate = useAccountGateStore()

/** Both fields carry the red underline for a form-level rejection. */
const fieldsInvalid = computed(() => form.formError.value !== null)

function handleEdit() {
  form.notifyFieldEdited('form')
}

async function handleSubmit() {
  await form.submit(async () => {
    try {
      const res = await useAuthStore().auth({
        email: email.value.trim(),
        password: password.value,
      })

      if (res.status === 200) {
        toast.show({ message: t('auth.toast.signed_in'), tone: 'success' })
        authModal.hideAuthModal()
        emits('closeModal')
      }
    } catch (error) {
      const mapped = mapAuthError(error, 'login')
      if (mapped.scope === 'network') {
        toast.show({ message: t(mapped.messageKey), tone: 'warning' })
      } else {
        form.setServerError(mapped)
      }
    }
  })
}
</script>

<template>
  <div class="fair-auth-panel">
    <form
      class="fair-auth-form"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <AuthErrorBanner
        v-if="!form.formError.value && accountGate.sessionNotice === 'token_revoked'"
        :message="t('account.session.revoked')"
        data-testid="auth-session-revoked"
      />
      <AuthErrorBanner
        v-if="!form.formError.value && accountGate.sessionNotice === 'user_not_provisioned'"
        :message="t('account.session.not_provisioned')"
        data-testid="auth-session-not-provisioned"
      />
      <AuthErrorBanner
        v-if="form.formError.value"
        :message="t(form.formError.value.messageKey)"
      />

      <AuthField
        v-model="email"
        input-id="auth-login-email"
        type="email"
        autocomplete="username"
        :label="t('auth.login.email_label')"
        :invalid="fieldsInvalid"
        @update:model-value="handleEdit"
      />

      <AuthField
        v-model="password"
        input-id="auth-login-password"
        type="password"
        autocomplete="current-password"
        :label="t('auth.login.password_label')"
        :invalid="fieldsInvalid"
        @update:model-value="handleEdit"
      />

      <div class="fair-auth-form__link-row">
        <button
          type="button"
          class="fair-auth-form__link"
          @click="authModal.showAuthModal('forgotPassword')"
        >
          {{ t('auth.login.forgot_link') }}
        </button>
      </div>

      <FairButton
      class="fair-auth-cta"
      size="auto"
        type="submit"
        :label="t('auth.login.submit')"
        :disabled="form.submitDisabled.value"
      />
    </form>

    <AuthSocialRow @signed-in="emits('closeModal')" />

    <AuthFooterSwitch
      :prompt="t('auth.login.no_account')"
      :action-label="t('auth.login.sign_up_link')"
      @switch="authModal.showAuthModal('signUp')"
    />
  </div>
</template>
